import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { getResponsiveSize } from '../../utils';
import AccordionComponent from '../UI/AccordionExpandIcon/AccordionComponent';
import { PractitionerMediaPreviewHost } from '../PractitionerMediaPreviewPlayer/PractitionerMediaPreviewReversePortal';
import { PractitionerCameraHost } from '../PractitionerCameraPreview/PractitionerCameraReversePortal';
import { PractitionerGameHost } from '../PractitionerGamePreview/PractitionerGameReversePortal';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import ViewSwapToggle from '../ControlsPractitioner/ViewSwapToggle/ViewSwapToggle';
import { Theme } from '../PhaserApp/enums/Themes';
import { getPractitionerViewLayout } from '../../utils/practitionerViewLayout';
import { ControlPanelFooterSX } from './styles';
interface ContainerProps {
  sendThemeCommand: (value: number) => void;
  isGameLoading: boolean;
  isCallActive: boolean;
  mediaPreviewPortalNode: HTMLDivElement | null;
  cameraPortalNode: HTMLDivElement | null;
  gamePortalNode: HTMLDivElement | null;
}

const ControlPanelFooter: React.FC<ContainerProps> = ({
  sendThemeCommand,
  isGameLoading,
  isCallActive,
  mediaPreviewPortalNode,
  cameraPortalNode,
  gamePortalNode,
}) => {
  const { viewSwapped, setViewSwapped, isMediaLoaded, hasBeenSaved, theme } = usePractitionerControlsContext();
  const { hasDevice } = useAppContext();
  const hasMedia = Boolean(isMediaLoaded && hasBeenSaved);
  // Same gate as ViewSwapToggle, plus the EMDR-VR check: headset sessions don't use the
  // web view swap, so the panel must keep its plain collapse-on-click behaviour there.
  const canSwapView = !hasDevice && (isCallActive || hasMedia);
  const { footerHasCamera, footerHasMedia, footerHasGame } = getPractitionerViewLayout({
    isCallActive,
    hasMedia,
    viewSwapped,
  });
  const [isChildView, setIsChildView] = useState<boolean>(false);
  const [isInitialRender, setIsInitialRender] = useState<boolean>(true);
  const accordionRef = useRef<HTMLDivElement | null>(null);
  const footerGameWrapperRef = useRef<HTMLDivElement | null>(null);
  const [toggleValue, setToggleValue] = useState<boolean>(() => {
    // Load initial expanded state from localStorage
    const storedState = localStorage.getItem('accordion_client-preview');
    return storedState ? JSON.parse(storedState) : true; //
  });
  const { footerSx } = ControlPanelFooterSX;
  useEffect(() => {
    if (!isInitialRender) {
      sendThemeCommand(isChildView ? Theme.Child : Theme.Default);
    } else {
      setIsInitialRender(false);
    }
  }, [isChildView]);

  const toggleView = () => {
    // Only hide the Phaser canvas when it currently lives in the footer.
    // When the game has been promoted to the main area, the accordion collapse
    // should not affect it — only the footer's own height changes.
    const canvas = footerGameWrapperRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
    const container = canvas?.parentElement ?? null;

    if (!toggleValue) {
      if (container) container.style.visibility = '';
      if (accordionRef.current) accordionRef.current.style.height = getResponsiveSize(200);
    } else {
      if (container) container.style.visibility = 'hidden';
      if (accordionRef.current) accordionRef.current.style.height = '0';
    }

    const newToggleValue = !toggleValue;
    setToggleValue(newToggleValue);
    localStorage.setItem('accordion_client-preview', JSON.stringify(newToggleValue));
  };

  return (
    <Box sx={footerSx} component="footer">
      <AccordionComponent
        isIcon={false}
        isClientPreview={true}
        setToggle={toggleView}
        toggleValue={toggleValue}
        title={'client-preview'}
        isExpanded={true}
        // Child landscape belongs to the game: only show it in the footer while the
        // game lives here. When promoted to the main area the footer reverts to grey
        // so the landscape isn't duplicated in both places.
        childThemeActive={theme === Theme.Child && footerHasGame}
        lightThemeActive={theme === Theme.Light && footerHasGame}
        headerAction={!hasDevice ? <ViewSwapToggle isCallActive={isCallActive} hasMedia={hasMedia} /> : null}
        onSurfaceClick={canSwapView ? () => setViewSwapped(!viewSwapped) : undefined}
      >
        <div ref={accordionRef} className="content">
          {!isGameLoading ? (
            <>
              <PractitionerCameraHost portalNode={cameraPortalNode} isActive={footerHasCamera} isOverlay />
              <PractitionerMediaPreviewHost portalNode={mediaPreviewPortalNode} isActive={footerHasMedia} isOverlay />
              {/*
                z-index 10: must beat PractitionerMediaPreviewPlayer's internal zIndex: 5.
                Camera/media host wrappers themselves have z-index: auto (don't create a
                stacking context), so the media player's inner zIndex: 5 leaks into the
                parent's stacking context. The footer game wrapper needs to be above that.
              */}
              <div
                ref={footerGameWrapperRef}
                style={{ position: 'relative', width: '100%', height: '100%', zIndex: 10 }}
              >
                <PractitionerGameHost portalNode={gamePortalNode} isActive={footerHasGame} />
              </div>
            </>
          ) : (
            <Box className="spinner-container">
              <CircularProgress color="inherit" />
            </Box>
          )}
        </div>
      </AccordionComponent>
    </Box>
  );
};

export default ControlPanelFooter;
